import Vue from 'vue'
import moment from 'moment-timezone'
import choiceFilter from 'utils/choiceFilter'
import { SEC_TYPE } from 'const/stock'
import * as types from 'store/mutation-types'
import isObject from 'lodash/isObject'
import parseBsTradeRequestUrl from 'utils/parseBsTradeRequestUrl'
import formatAnalyticsAsset from '../utils/formatAnalyticsAsset'
import { CURRENCY_TYPE } from 'const/trade'
import getLocalSymbol from 'utils/getLocalSymbol'
import setOptionRequestParams from 'utils/setOptionRequestParams'
import getLocalFixedQuoteDetail from 'utils/getLocalFixedQuoteDetail'

const state = {
  asset: null, // 资产
  exchangeRates: null, // 换美元汇率
  holdings: [] // 持仓
}

function getDateRange(startDate, endDate) {
  startDate = startDate
    ? moment().subtract(moment.duration(startDate))
    : moment().subtract(90, 'days')
  endDate = endDate || moment()

  return [startDate.format('YYYYMMDD'), endDate.format('YYYYMMDD')]
}

const mutations = {
  [types.UPDATE_USER_ASSET](state, asset) {
    Vue.set(state, 'asset', asset)
  },
  [types.UPDATE_USER_HOLDINGS_LIST](state, holdings) {
    if (Array.isArray(holdings)) {
      if (holdings.length === 0) return
      Vue.set(
        state,
        'holdings',
        holdings.map(hold => ({
          ...hold,
          digits: choiceFilter(hold)
        }))
      )
    } else if (isObject(holdings)) {
      const index = state.holdings.findIndex(d => {
        return d.symbol === holdings.symbol && d.secType !== SEC_TYPE.OPT
      })

      if (index >= 0) {
        state.holdings.splice(index, 1, {
          ...state.holdings[index],
          ...holdings
        })
      } else {
        state.holdings.push(holdings)
      }
    }
  },
  [types.UPDATE_EXCHANGE_RATE](state, exchangeRates) {
    state.exchangeRates = exchangeRates
  },
  [types.DELETE_USER_HOLDING_ASSET](state) {
    Vue.set(state, 'holdings', [])
    Vue.set(state, 'asset', null)
  }
}

const getters = {
  asset(state) {
    return state.asset
  },
  holdings(state) {
    return state.holdings
  },
  exchangeRates(state) {
    return state.exchangeRates
  }
}

const actions = {
  getUserAssetAnalytics({ getters }, dateRange = []) {
    const [startDate, endDate] = getDateRange(...dateRange)
    if (getters.isIb) {
      return Vue.http
        .get('~trade/portfolio/account_analyze', {
          params: { startDate, endDate }
        })
        .then(res => {
          if (res.data.ret === 0) {
            const ranking = res.data?.data.ranking
            if (ranking) {
              ['maxLose', 'maxWin', 'mostActive'].forEach(name => {
                if (ranking[name]) {
                  ranking[name].nameCN = ranking[name].nameCn
                }
              })
            }
            return res.data.data
          }
          throw res.data
        })
    }
    if (getters.isMarsco) {
      return Vue.http
        .get('~omnibusTrade/up/account-analyze', {
          params: { startDate, endDate }
        })
        .then(res => {
          if (res.data.status === 'ok' && res.data.data) {
            return formatAnalyticsAsset(res.data.data, true)
          }
          throw res.data
        })
    }
    return Vue.http
      .get('~virtualTrade/analytics/asset', {
        params: {
          since_date: startDate,
          to_date: endDate,
          seg_type: 'SEC'
        }
      })
      .then(res => {
        if (res.data.status === 'ok' && res.data.data) {
          return formatAnalyticsAsset(res.data.data, false)
        }
        throw res.data
      })
  },
  getUserDepositWithdrawal({ getters }, dateRange = []) {
    const [startDate, endDate] = getDateRange(...dateRange)
    if (getters.isIb) {
      return Vue.http
        .get('~trade/portfolio/deposit_history', {
          params: { startDate, endDate }
        })
        .then(res => {
          if (res.data.ret === 0) {
            return res.data.items || []
          }
          throw res.data
        })
    }
    return Vue.http
      .get(
        parseBsTradeRequestUrl('/funds/deposit_withdrawal', getters.isVirtual)
      )
      .then(res => {
        if (res.data.status === 'ok') {
          return res.data.data || []
        }
        throw res.data
      })
  },
  getUserAssets({ commit, getters }) {
    if (getters.isIb) {
      return Vue.http.get('~trade/portfolio/asset').then(({ data: res }) => {
        if (res.ret === 0 && res.items && res.items.length) {
          commit(types.UPDATE_USER_ASSET, res.items[0])
          return res.items[0]
        }
        throw res
      })
    } else {
      return Vue.http
        .get(parseBsTradeRequestUrl('/portfolio/asset', getters.isVirtual))
        .then(({ data: res }) => {
          if (res.status === 'ok' && res.data) {
            res.data.currency = res.data.currency || CURRENCY_TYPE.US
            commit(types.UPDATE_USER_ASSET, res.data)
            return res.data
          }
          throw res
        })
    }
  },
  getUserHoldings({ commit, getters }) {
    if (getters.isIb) {
      return Vue.http
        .get('~trade/portfolio/position', { params: { secType: 'ALL' } })
        .then(res => {
          if (res.data.ret === 0) {
            const holdings = getLocalFixedQuoteDetail(res.data.items || [])
            commit(types.UPDATE_USER_HOLDINGS_LIST, holdings)
            return holdings
          }
          throw res.data
        })
    }
    return Vue.http
      .get(parseBsTradeRequestUrl('/portfolio/positions', getters.isVirtual))
      .then(res => {
        if (res.data.status === 'ok') {
          const holdings = getLocalFixedQuoteDetail(res.data.data || [])
          commit(types.UPDATE_USER_HOLDINGS_LIST, holdings)
          return holdings
        }
        throw res.data
      })
  },
  getUserPnl({ getters }) {
    if (getters.isIb) {
      return Vue.http.get('~trade/portfolio/stat_brief').then(res => {
        if (res.data.ret === 0) {
          return getLocalFixedQuoteDetail(res.data.items || [])
        }
        throw res.data
      })
    }
    return Vue.http
      .get(parseBsTradeRequestUrl('/analytics/trade/list', getters.isVirtual))
      .then(res => {
        if (res.data.status === 'ok') {
          return getLocalFixedQuoteDetail(res.data.data || [])
        }
        throw res.data
      })
  },
  // 获取用户分红历史
  getUserDividends({ getters }, dateRange = []) {
    const [startDate, endDate] = getDateRange(...dateRange)
    if (getters.isIb) {
      return Vue.http
        .get('~trade/portfolio/dividend_history', {
          params: { startDate, endDate }
        })
        .then(res => {
          if (res.data.ret === 0) {
            return res.data.items || []
          }
          throw res.data
        })
    }
    return Vue.http
      .get(parseBsTradeRequestUrl('/dividends', getters.isVirtual), {
        params: { since_date: startDate, to_date: endDate }
      })
      .then(res => {
        if (res.data.status === 'ok') {
          return res.data.data || []
        }
        throw res.data
      })
  },
  // 获取汇率
  getUserExchangeRate({ commit }, currency = 'HKD,CNH,EUR,GBP') {
    return Vue.http
      .get('~trade/stock_info/exchangeRate', {
        params: { currency }
      })
      .then(res => {
        if (res.data.ret === 0 && res.data.items) {
          const rates = res.data.items.reduce((acc, cur) => {
            acc[cur[0]] = cur[1]
            return acc
          }, {})
          commit(types.UPDATE_EXCHANGE_RATE, rates)
          return rates
        }
        throw res.data
      })
  },
  getStockDividends({ getters }, payload) {
    const params = { symbol: payload.symbol }
    if (getters.isIb) {
      return Vue.http
        .get('~trade/portfolio/dividend_history', {
          params
        })
        .then(res => {
          if (res.data.ret === 0) {
            return res.data.items || []
          }
          throw res.data
        })
    }
    if (payload.startDate) params.since_date = payload.startDate
    if (payload.endDate) params.to_date = payload.endDate
    return Vue.http
      .get(parseBsTradeRequestUrl('/dividends', getters.isVirtual), {
        params
      })
      .then(res => {
        if (res.data.status === 'ok') {
          return res.data.data || []
        }
        throw res.data
      })
  },
  getStockPnl({ getters }, payload) {
    const params = { symbol: payload.symbol }
    if (getters.isIb) {
      params.symbol = getLocalSymbol(payload)
      if (payload.startDate) params.startDate = payload.startDate
      if (payload.endDate) params.endDate = payload.endDate
      return Vue.http
        .get(`~trade/portfolio/pnl/${params.symbol}`, {
          params
        })
        .then(res => {
          if (res.data.ret === 0) {
            return (res.data.items || []).reduce(
              (acc, cur) => {
                const {
                  buyMarketValue,
                  unrealizedPnL,
                  position,
                  realizedPnL,
                  sellMarketValue,
                  trades,
                  ...pnl
                } = acc
                return {
                  ...pnl,
                  position: position + cur.position,
                  realizedPnL: realizedPnL + cur.realizedPnL,
                  unrealizedPnL: unrealizedPnL + cur.unrealizedPnL,
                  buyMarketValue: buyMarketValue + cur.buyMarketValue,
                  sellMarketValue: sellMarketValue + cur.sellMarketValue,
                  trades: trades.concat(cur.trades)
                }
              },
              {
                trades: [],
                position: 0,
                realizedPnL: 0,
                unrealizedPnL: 0,
                buyMarketValue: 0,
                sellMarketValue: 0
              }
            )
          }
          throw res.data
        })
    }
    params.sec_type = payload.secType || SEC_TYPE.STK
    if (payload.secType === SEC_TYPE.OPT) {
      setOptionRequestParams(params, payload)
    }
    return Vue.http
      .get(
        parseBsTradeRequestUrl('/analytics/trade/detail', getters.isVirtual),
        { params }
      )
      .then(res => {
        if (res.data.status === 'ok' && res.data.data) {
          return res.data.data
        }
        throw res.data
      })
  }
}

export default {
  state,
  actions,
  mutations,
  getters
}
